import { MoreVertical, Send, Ban, Trash2, RefreshCw, Mail, Clock, CheckCircle } from 'lucide-react';
// [DISABLED] Expiration — décommentez pour réactiver
// import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Badge de statut pour les invitations
 */
function InvitationStatusBadge({ status }) {
    const { t } = useTranslation();
    const statusConfig = {
        pending: {
            label: t('procedures:enAttente'),
            bgColor: 'bg-warning/10',
            textColor: 'text-warning',
            icon: Clock,
        },
        active: {
            label: t('procedures:active'),
            bgColor: 'bg-info/10',
            textColor: 'text-info',
            icon: Mail,
        },
        completed: {
            label: t('procedures:completee'),
            bgColor: 'bg-success/10',
            textColor: 'text-success',
            icon: CheckCircle,
        },
        // [DISABLED] Expiration — décommentez pour réactiver le statut "Expirée"
        // expired: {
        //     label: 'Expirée',
        //     bgColor: 'bg-warning/10',
        //     textColor: 'text-warning',
        //     icon: AlertTriangle,
        // },
        revoked: {
            label: t('procedures:revoquee'),
            bgColor: 'bg-muted',
            textColor: 'text-dark/50',
            icon: Ban,
        },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}
        >
            <Icon className="h-3 w-3" />
            {config.label}
        </span>
    );
}

/**
 * Formate une date ISO en format français
 */
function formatDate(dateString) {
    if (!dateString) return '-';
    try {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return dateString;
    }
}

/**
 * [DISABLED] Expiration — décommentez pour réactiver la vérification d'expiration
 */
function isExpired(expiresAt) {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
}

/**
 * Crée les définitions de colonnes pour la table des invitations
 *
 * @param {Object} options
 * @param {function} options.onResendEmail - Callback pour renvoyer l'email
 * @param {function} options.onRevoke - Callback pour révoquer l'invitation
 * @param {function} options.onActivate - Callback pour réactiver l'invitation
 * @param {function} options.onDelete - Callback pour supprimer l'invitation
 * @param {string|null} options.actionInProgress - ID de l'invitation en cours d'action
 * @returns {import('@tanstack/react-table').ColumnDef[]}
 */
export function createInvitationsColumns({ onResendEmail, onRevoke, onActivate, onDelete, actionInProgress }) {
    return [
        {
            accessorKey: 'email',
            header: () => (
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{i18n.t('procedures:email')}</span>
            ),
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="font-medium text-foreground truncate">{row.original.email}</span>
                </div>
            ),
            size: 250,
        },
        {
            accessorKey: 'status',
            header: () => (
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{i18n.t('procedures:statut')}</span>
            ),
            cell: ({ row }) => {
                // [DISABLED] Expiration — décommentez pour réactiver le statut "Expirée" automatique
                // const invitation = row.original;
                // const displayStatus =
                //     isExpired(invitation.expires_at) &&
                //     invitation.status !== 'completed' &&
                //     invitation.status !== 'revoked'
                //         ? 'expired'
                //         : invitation.status;
                // return <InvitationStatusBadge status={displayStatus} />;
                return <InvitationStatusBadge status={row.original.status} />;
            },
            size: 120,
        },
        {
            accessorKey: 'folder',
            header: () => (
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{i18n.t('procedures:dossier')}</span>
            ),
            cell: ({ row }) => (
                <span className="text-muted-foreground truncate">{row.original.folder?.name || '-'}</span>
            ),
            size: 180,
        },
        {
            accessorKey: 'created_at',
            header: () => (
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{i18n.t('procedures:creeeLe')}</span>
            ),
            cell: ({ row }) => <span className="text-muted-foreground">{formatDate(row.original.created_at)}</span>,
            size: 100,
        },
        // [DISABLED] Expiration — décommentez pour réactiver la colonne "Expire le"
        {
            accessorKey: 'expires_at',
            header: () => (
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Expire le</span>
            ),
            cell: ({ row }) => {
                const expiresAt = row.original.expires_at;
                const expired = isExpired(expiresAt);
                return (
                    <span className={expired ? 'text-destructive' : 'text-muted-foreground'}>
                        {formatDate(expiresAt)}
                    </span>
                );
            },
            size: 100,
        },
        {
            id: 'actions',
            header: () => null,
            cell: ({ row }) => {
                const invitation = row.original;
                const isActionInProgress = actionInProgress === invitation.uniq_id;

                return (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="plain"
                                color="neutral"
                                size="icon"
                                className="h-8 w-8"
                                disabled={isActionInProgress}
                            >
                                {isActionInProgress ? (
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                    <MoreVertical className="h-4 w-4" />
                                )}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            {invitation.status !== 'completed' && invitation.status !== 'revoked' && invitation.status !== 'active' && (
                                <DropdownMenuItem onClick={() => onResendEmail(invitation)}>
                                    <Send className="h-4 w-4 mr-2" />
                                    {i18n.t('procedures:renvoyerLEmail')}
                                </DropdownMenuItem>
                            )}

                            {invitation.status === 'revoked' && (
                                <DropdownMenuItem onClick={() => onActivate(invitation)}>
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    {i18n.t('procedures:reactiver')}
                                </DropdownMenuItem>
                            )}

                            {(invitation.status === 'pending' || invitation.status === 'active') && (
                                <DropdownMenuItem onClick={() => onRevoke(invitation)} className="text-warning">
                                    <Ban className="h-4 w-4 mr-2" />
                                    {i18n.t('procedures:revoquer')}
                                </DropdownMenuItem>
                            )}

                            <DropdownMenuItem onClick={() => onDelete(invitation)} className="text-destructive">
                                <Trash2 className="h-4 w-4 mr-2" />
                                {i18n.t('procedures:supprimer')}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            },
            size: 50,
        },
    ];
}
